import React from 'react';
import {
  type ParsedUsage,
  type ParsedUsageBucket,
  type ParsedUsageCase,
  type ParsedUsageRole,
  type UsageStatusHint,
  parseUsageByCase,
  parseUsageSummary,
} from '../utils/usageSummary';
import { splitCaseId } from '../utils/scoresByCase';
import './UsageBreakdown.css';

/**
 * Расход прогона: итог и разбивка по процессам.
 *
 * Один компонент на все экраны, потому что сводка везде одной формы, а раньше
 * каждый экран показывал свой огрызок: где одно число, где только деньги двух
 * ролей из трёх, где вообще прочерк из-за неверного пути в объекте.
 *
 * Экран общий с NLTPatchPanel и EvaluationRunResultView — их вид трогать
 * нельзя, поэтому колонки «Calls»/«Model» рисуются, только если хоть одна
 * корзина реально сообщила счётчик вызовов или имя модели: у прогонов патча и
 * оценки таких полей нет, и колонки там просто не появляются.
 */

function formatTokens(value: number): string {
  return Math.round(value).toLocaleString();
}

function formatCost(value: number): string {
  if (value === 0) return '$0.00';
  if (value < 0.01) return `$${value.toFixed(4)}`;
  return `$${value.toFixed(2)}`;
}

function formatDuration(value: number): string {
  if (value <= 0) return '—';
  if (value < 60) return `${value.toFixed(1)}s`;
  const m = Math.floor(value / 60);
  const s = Math.round(value % 60);
  return `${m}m ${s}s`;
}

function TokenCell({ bucket }: { bucket: ParsedUsageBucket }) {
  if (!bucket.tokensKnown) {
    // Ноль здесь читался бы как «прогон ничего не сжёг», а он сжёг — просто
    // счётчики затёрли при записи.
    return <span className="usage-bd__tokens usage-bd__tokens--unknown">—</span>;
  }
  const hasSplit = bucket.promptTokens > 0 || bucket.completionTokens > 0;
  return (
    <>
      <span className="usage-bd__tokens">{formatTokens(bucket.totalTokens)}</span>
      {hasSplit ? (
        <span className="usage-bd__split">
          {formatTokens(bucket.promptTokens)} in / {formatTokens(bucket.completionTokens)} out
          {bucket.cachedTokens > 0 ? ` / ${formatTokens(bucket.cachedTokens)} cached` : ''}
        </span>
      ) : null}
    </>
  );
}

function callsCell(bucket: ParsedUsageBucket): string {
  const calls = bucket.extras.llm_call_count;
  return calls != null ? formatTokens(calls) : '—';
}

/** Хоть одна корзина (включая вложенные части) сообщила вызовы или модель. */
function anyBucketHasCallsOrModel(roles: ParsedUsageRole[], total: ParsedUsageBucket): boolean {
  const flatten = (r: ParsedUsageRole): ParsedUsageBucket[] => [r, ...r.parts.flatMap(flatten)];
  return [...roles.flatMap(flatten), total].some((b) => b.extras.llm_call_count != null || !!b.modelName);
}

function UsageRow({ row, nested, showExtra }: { row: ParsedUsageRole; nested?: boolean; showExtra: boolean }) {
  return (
    <>
      <tr className={nested ? 'usage-bd__row usage-bd__row--nested' : 'usage-bd__row'}>
        <th scope="row">{row.label}</th>
        <td><TokenCell bucket={row} /></td>
        <td>{formatCost(row.totalCost)}</td>
        <td>{formatDuration(row.durationS)}</td>
        {showExtra ? <td>{callsCell(row)}</td> : null}
        {showExtra ? <td>{row.modelName ?? '—'}</td> : null}
      </tr>
      {row.parts.map((part) => (
        <UsageRow key={`${row.role}:${part.role}`} row={part} nested showExtra={showExtra} />
      ))}
    </>
  );
}

/** Одна таблица расхода: строки процессов плюс итог. Переиспользуется и для
 *  сводки всего сабмишена, и для разбивки по датасету — форма одна и та же. */
function UsageTable({ roles, total, caption }: { roles: ParsedUsageRole[]; total: ParsedUsageBucket; caption?: string }) {
  const showExtra = anyBucketHasCallsOrModel(roles, total);
  return (
    <table className="usage-bd__table">
      {caption ? <caption className="usage-bd__caption">{caption}</caption> : null}
      <thead>
        <tr>
          <th scope="col">Process</th>
          <th scope="col">Tokens</th>
          <th scope="col">Cost</th>
          <th scope="col">Time</th>
          {showExtra ? <th scope="col">Calls</th> : null}
          {showExtra ? <th scope="col">Model</th> : null}
        </tr>
      </thead>
      <tbody>
        {roles.map((row) => (
          <UsageRow key={row.role} row={row} showExtra={showExtra} />
        ))}
        <tr className="usage-bd__row usage-bd__row--total">
          <th scope="row">Total</th>
          <td><TokenCell bucket={total} /></td>
          <td>{formatCost(total.totalCost)}</td>
          <td>{formatDuration(total.durationS)}</td>
          {showExtra ? <td>{callsCell(total)}</td> : null}
          {showExtra ? <td>{total.modelName ?? '—'}</td> : null}
        </tr>
      </tbody>
    </table>
  );
}

/** Заголовок группы: имя датасета, если оно известно.
 *
 *  `case_id` имеет вид `<источник>:<идентификатор>`, и в заголовке стоял он
 *  целиком — по uuid не понять, о каком датасете речь. Разбор общий с таблицей
 *  скоров: две копии правила деления разошлись бы, и одна и та же строка
 *  показывалась бы в двух блоках по-разному. */
function caseCaption(caseId: string, datasetNames?: Record<string, string>): string {
  return datasetNames?.[splitCaseId(caseId).label] ?? caseId;
}

function UsageCaseGroup({ item, datasetNames }: { item: ParsedUsageCase; datasetNames?: Record<string, string> }) {
  return <UsageTable roles={item.roles} total={item.total} caption={caseCaption(item.caseId, datasetNames)} />;
}

export interface UsageBreakdownProps {
  /** Сырая сводка с сервера. */
  usage: unknown;
  /**
   * Сырое `usage_by_case`: расход по каждому датасету метадатасета. Не задано —
   * блока разбивки нет, и это не то же самое, что пустая разбивка: у прогона на
   * одном датасете её и не должно быть.
   */
  usageByCase?: unknown;
  /**
   * Авторитетный `usage_status` сабмишена (`known`/`redacted`/`absent`), когда
   * бэкенд его прислал. Перекрывает угадывание по строке `"<redacted>"`:
   * поменяется маска секретов — эвристика замолчит, а бэкенд не соврёт.
   */
  usageStatus?: UsageStatusHint;
  /** `dataset_id` → имя из каталога. Нет имени — остаётся `case_id`: подпись,
   *  которой нет, лучше выдуманной. */
  datasetNames?: Record<string, string>;
  /**
   * Сырой `cost_status` с сервера. `unknown_model` означает, что модели нет в
   * прейскуранте — стоимость собрана из того, что удалось оценить, и занижена.
   * Молчать об этом нельзя: ноль или маленькое число читается как «дёшево».
   */
  costStatus?: string | null;
  title?: string;
  /**
   * Что показать, когда расхода не сообщили вовсе. Отсутствие сводки и нулевой
   * расход — разные вещи, поэтому по умолчанию блок просто не рисуется.
   */
  emptyText?: string;
}

const UsageBreakdown: React.FC<UsageBreakdownProps> = ({
  usage,
  usageByCase,
  usageStatus,
  costStatus,
  datasetNames,
  title = 'Usage',
  emptyText,
}) => {
  const parsed: ParsedUsage | null = React.useMemo(() => parseUsageSummary(usage, usageStatus), [usage, usageStatus]);
  const cases = React.useMemo(() => parseUsageByCase(usageByCase), [usageByCase]);

  // Сабмишен может прислать разбивку по датасетам без общего `usage_totals`
  // (или наоборот). Раньше отсутствие `parsed` гасило блок целиком — разбивка
  // молча пропадала вместе с итогом, которого просто не было.
  if (!parsed && cases.length === 0) {
    return emptyText ? <p className="usage-bd__empty">{emptyText}</p> : null;
  }

  return (
    <div className="usage-bd">
      {parsed ? (
        <>
          <div className="usage-bd__head">
            <span className="usage-bd__title">{title}</span>
            <span className="usage-bd__headline">
              {parsed.total.tokensKnown ? `${formatTokens(parsed.total.totalTokens)} tokens` : 'tokens unknown'}
              {' · '}
              {formatCost(parsed.total.totalCost)}
              {parsed.llmCalls != null ? ` · ${formatTokens(parsed.llmCalls)} calls` : ''}
            </span>
          </div>

          <UsageTable roles={parsed.roles} total={parsed.total} />
        </>
      ) : (
        <div className="usage-bd__head">
          <span className="usage-bd__title">{title}</span>
        </div>
      )}

      {cases.length > 0 ? (
        <div className="usage-bd__cases">
          <span className="usage-bd__title">By dataset</span>
          {cases.map((item) => (
            <UsageCaseGroup key={item.caseId} item={item} datasetNames={datasetNames} />
          ))}
          <p className="usage-bd__note">
            These are the final per-dataset measurements only. The optimisation cycle itself is
            not split per dataset — neither the algorithm's work (it edits one prompt for all of
            them at once) nor the actor and judge calls made inside the cycle. That is why the
            datasets sum to less than the total above, and the difference is the cycle.
          </p>
        </div>
      ) : null}

      {parsed && parsed.roles.length === 0 ? (
        <p className="usage-bd__note">The server sent no per-process breakdown — only the total.</p>
      ) : null}
      {parsed && parsed.totalDerived ? (
        <p className="usage-bd__note">The total is summed from the processes: the server sent none of its own.</p>
      ) : null}
      {parsed && parsed.tokensRedacted ? (
        <p className="usage-bd__note usage-bd__note--warn">
          Token counters were redacted on write — this run predates 2026-09-08. The cost is real;
          the token numbers cannot be recovered.
        </p>
      ) : null}
      {parsed && parsed.unknownLlmCalls != null && parsed.unknownLlmCalls > 0 ? (
        <p className="usage-bd__note usage-bd__note--warn">
          {formatTokens(parsed.unknownLlmCalls)} calls with no usage recorded — the total is understated.
        </p>
      ) : null}
      {costStatus === 'unknown_model' ? (
        <p className="usage-bd__note usage-bd__note--warn">
          The model has no price on record — the cost is understated. The token counts are correct.
        </p>
      ) : null}
    </div>
  );
};

export default UsageBreakdown;
